// src\hooks\useToast.jsx
import { useState, useCallback, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';

// Create Toast Context
const ToastContext = createContext();

// Toast Hook
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

// Toast Provider Component
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'info', duration = 5000) => {
    // Use a more unique ID with timestamp + random + counter
    const id = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const toast = { id, message, type, duration };

    setToasts(prev => [...prev, toast]);

    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, duration);
    }

    return id;
  }, []);

  const hideToast = useCallback(id => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider
      value={{ toasts, showToast, hideToast, clearToasts }}
    >
      {children}
      <ToastContainer toasts={toasts} onHide={hideToast} />
    </ToastContext.Provider>
  );
};

// Toast Container Component
const ToastContainer = ({ toasts, onHide }) => {
  if (!toasts.length) return null;

  const getTypeStyles = type => {
    switch (type) {
      case 'success':
        return 'bg-green-500 text-white';
      case 'error':
        return 'bg-red-500 text-white';
      case 'warning':
        return 'bg-yellow-500 text-white';
      default:
        return 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900';
    }
  };

  return createPortal(
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`${getTypeStyles(toast.type)} px-4 py-3 rounded-lg shadow-lg animate-fade-in-up min-w-[200px] max-w-sm cursor-pointer`}
          onClick={() => onHide(toast.id)}
        >
          <p className="text-sm">{toast.message}</p>
        </div>
      ))}
    </div>,
    document.body,
  );
};
